(function () {
  /** @param {Element} el */
  function isVisible(el) {
    if (typeof el.checkVisibility === 'function') return el.checkVisibility();
    // Fallback: display:none on the element or any ancestor nulls offsetParent.
    return el instanceof HTMLElement ? el.offsetParent !== null : true;
  }

  /** @param {ParentNode} container */
  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) =>
        !el.hasAttribute('hidden') &&
        el.getAttribute('aria-hidden') !== 'true' &&
        // Controls inside hidden containers (e.g. the click & collect picker
        // views) must not become the trap's first/last stop — focusing an
        // invisible element fails and Tab gets stuck.
        isVisible(el)
    );
  }

  /**
   * @param {HTMLElement} container
   * @param {() => void} onEscape
   */
  function createFocusTrap(container, onEscape) {
    /** @param {KeyboardEvent} event */
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(container);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }

  /**
   * Shared dialog binding — modals and drawers are the same component with
   * different data-attribute names (deduplicated; the pair had drifted).
   * @param {HTMLDialogElement} dialog
   * @param {'modal' | 'drawer'} kind
   */
  function bindDialog(dialog, kind) {
    const boundFlag = kind === 'drawer' ? 'mapDrawerBound' : 'mapModalBound';
    if (dialog.dataset[boundFlag] === 'true') return;
    dialog.dataset[boundFlag] = 'true';

    /** @type {(() => void) | null} */
    let releaseTrap = null;
    /** @type {HTMLElement | null} */
    let previousFocus = null;
    /** @type {number | null} */
    let closeTimer = null;

    const DRAWER_MS = 200;

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Release on the native close event so the trap is also torn down when
    // other code calls dialog.close() directly (cart-drawer, click & collect) —
    // previously those paths leaked the keydown listener and lost focus restore.
    dialog.addEventListener('close', () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      dialog.classList.remove('is-open');
      releaseTrap?.();
      releaseTrap = null;
      previousFocus?.focus();
      previousFocus = null;
    });

    function finishClose() {
      closeTimer = null;
      if (kind === 'drawer' && dialog.classList.contains('is-open')) return;
      if (dialog.open) dialog.close();
    }

    /**
     * @param {{ immediate?: boolean }} [options]
     */
    function close(options = {}) {
      if (!dialog.open && !dialog.classList.contains('is-open')) return;

      const immediate = Boolean(options.immediate) || prefersReducedMotion() || kind !== 'drawer';
      dialog.classList.remove('is-open');

      if (immediate) {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = null;
        }
        if (dialog.open) dialog.close();
        return;
      }

      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(finishClose, DRAWER_MS);
    }

    function syncDrawerHeaderOffset() {
      const bar = document.querySelector('.site-header__bar');
      if (!(bar instanceof HTMLElement)) return;
      const height = Math.ceil(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--site-header-bar-offset', `${height}px`);
    }

    function open() {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      if (dialog.open && dialog.classList.contains('is-open')) return;

      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : previousFocus;
      if (kind === 'drawer') syncDrawerHeaderOffset();
      if (!dialog.open) dialog.showModal();

      if (kind === 'drawer') {
        void dialog.offsetWidth;
        dialog.classList.add('is-open');
      }

      if (!releaseTrap) releaseTrap = createFocusTrap(dialog, () => close());
      const first = getFocusable(dialog)[0];
      first?.focus();
    }

    dialog.addEventListener('map:drawer-open', open);
    dialog.addEventListener('map:drawer-close', (event) => {
      const detail = event instanceof CustomEvent ? event.detail : null;
      close(detail && typeof detail === 'object' ? detail : {});
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (target === dialog) {
        close();
        return;
      }
      if (target instanceof Element && target.closest(`[data-map-${kind}-close]`)) {
        close();
      }
    });

    document.querySelectorAll(`[data-map-${kind}-open="${dialog.id}"]`).forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        open();
      });
    });
  }

  /** @param {HTMLDialogElement} dialog */
  function bindModal(dialog) {
    bindDialog(dialog, 'modal');
  }

  /** @param {HTMLDialogElement} dialog */
  function bindDrawer(dialog) {
    bindDialog(dialog, 'drawer');
  }

  /** @param {HTMLElement} root */
  function initVehicleFindPanel(root) {
    if (root.dataset.vehicleFindInit === 'true') return;
    root.dataset.vehicleFindInit = 'true';

    const findMode = root.querySelector('[data-vehicle-find-mode]');
    const selectedMode = root.querySelector('[data-vehicle-selected-mode]');
    const accountPrompt = root.querySelector('[data-vehicle-account-prompt]');
    const changeBtn = root.querySelector('[data-vehicle-change]');
    const submitBtn = root.querySelector('[data-vehicle-find-submit]');
    const regoPanel = root.querySelector('[data-vehicle-find-rego-panel]');
    const regoInput = root.querySelector('[data-vehicle-find-rego]');
    const regoSearch = root.querySelector('[data-vehicle-find-rego-search]');
    const manualToggle = root.querySelector('[data-vehicle-find-manual-toggle]');
    const manualPanel = root.querySelector('[data-vehicle-find-manual]');
    /* WIL-568: plate lookup stays in the markup, off until an API exists. */
    const regoEnabled = root.dataset.vehicleRegoEnabled === 'true';
    const results = root.querySelector('[data-vehicle-rego-results]');
    const status = root.querySelector('[data-vehicle-find-status]');

    /** @param {string} message */
    function showStatus(message) {
      if (!(status instanceof HTMLElement)) return;
      status.textContent = message;
      status.hidden = false;
    }

    function hideStatus() {
      if (status instanceof HTMLElement) status.hidden = true;
    }

    function hideResults() {
      if (!(results instanceof HTMLElement)) return;
      results.replaceChildren();
      results.hidden = true;
    }

    function showManualLookup() {
      if (manualToggle instanceof HTMLButtonElement) manualToggle.setAttribute('aria-expanded', 'true');
      if (manualPanel instanceof HTMLElement) manualPanel.hidden = false;
      if (regoPanel instanceof HTMLElement) regoPanel.hidden = true;
      hideStatus();
      hideResults();
    }

    /**
     * @param {{ keepRego?: boolean, keepStatus?: boolean }} [options]
     */
    function expandManual(options = {}) {
      if (!regoEnabled) {
        showManualLookup();
        return;
      }
      if (!(manualToggle instanceof HTMLButtonElement) || !(manualPanel instanceof HTMLElement)) return;
      manualToggle.setAttribute('aria-expanded', 'true');
      manualPanel.hidden = false;
      if (!options.keepRego && regoPanel instanceof HTMLElement) regoPanel.hidden = true;
      if (!options.keepStatus) hideStatus();
      hideResults();
    }

    function collapseManual() {
      if (!regoEnabled) {
        showManualLookup();
        return;
      }
      if (manualToggle instanceof HTMLButtonElement) manualToggle.setAttribute('aria-expanded', 'false');
      if (manualPanel instanceof HTMLElement) manualPanel.hidden = true;
      if (regoPanel instanceof HTMLElement) regoPanel.hidden = false;
    }

    function readSavedVehicle() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem('selectedVehicle') || 'null');
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }

    function hasPersistedVehicle() {
      const saved = readSavedVehicle();
      if (saved?.make) return true;
      return window.location.pathname.includes('+make_');
    }

    function fillSelectedLabel() {
      const pill = root.querySelector('[data-vehicle-selected-mode] [data-vehicle-active]');
      const text = pill?.querySelector('[data-vehicle-active-label]');
      const saved = readSavedVehicle();
      if (!(pill instanceof HTMLElement) || !(text instanceof HTMLElement) || !saved?.make) return;
      if (!text.textContent) {
        text.textContent = [saved.make, saved.model, saved.year, saved.series].filter(Boolean).join(' ');
      }
      pill.hidden = false;
    }

    /**
     * @param {'find' | 'selected'} mode
     */
    function setMode(mode) {
      root.dataset.vehicleMode = mode;
      if (findMode instanceof HTMLElement) findMode.hidden = mode === 'selected';
      if (selectedMode instanceof HTMLElement) selectedMode.hidden = mode !== 'selected';
      if (accountPrompt instanceof HTMLElement) accountPrompt.hidden = mode !== 'selected';
      if (changeBtn instanceof HTMLButtonElement) changeBtn.hidden = mode !== 'selected';
      if (submitBtn instanceof HTMLButtonElement) submitBtn.hidden = mode === 'selected';
    }

    function syncMode() {
      if (root.dataset.vehicleEditing === 'true') {
        setMode('find');
        expandManual();
        return;
      }
      if (hasPersistedVehicle()) {
        fillSelectedLabel();
        setMode('selected');
        collapseManual();
        hideStatus();
        hideResults();
        return;
      }
      setMode('find');
      if (regoEnabled) collapseManual();
      else showManualLookup();
    }

    /**
     * Rego matches from APG — not wired until the plate service is approved.
     * @param {{ label: string, value?: string }[]} matches
     */
    function renderRegoResults(matches) {
      if (!(results instanceof HTMLElement)) return;
      results.replaceChildren();
      if (!matches.length) {
        results.hidden = true;
        return;
      }

      const name = results.dataset.vehicleRegoName || 'vehicle-rego-match';
      matches.forEach((match, index) => {
        const option = document.createElement('label');
        option.className = 'vehicle-find-panel__result';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.className = 'vehicle-find-panel__result-radio';
        radio.name = name;
        radio.value = match.value || match.label;
        radio.checked = index === 0;
        const text = document.createElement('span');
        text.className = 'vehicle-find-panel__result-label';
        text.textContent = match.label;
        option.append(radio, text);
        results.append(option);
      });
      results.hidden = false;
    }

    if (regoInput instanceof HTMLInputElement) {
      regoInput.addEventListener('input', () => {
        hideStatus();
        hideResults();
      });
      regoInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && regoSearch instanceof HTMLButtonElement) {
          event.preventDefault();
          regoSearch.click();
        }
      });
    }

    if (regoSearch instanceof HTMLButtonElement) {
      regoSearch.addEventListener('click', () => {
        const query = regoInput instanceof HTMLInputElement ? regoInput.value.trim() : '';
        hideResults();
        if (!query) {
          showStatus(root.dataset.vehicleRegoEmpty || 'Enter a registration number to search.');
          return;
        }
        /* Plate lookup is pending APG (WIL-382). Keep the typed plate,
           show the honest unavailable copy, and open the manual cascade. */
        showStatus(
          root.dataset.vehicleRegoUnavailable ||
            'Registration lookup is not available yet. Enter your details manually.'
        );
        expandManual({ keepRego: true, keepStatus: true });
      });
    }

    if (manualToggle instanceof HTMLButtonElement && manualPanel instanceof HTMLElement) {
      manualToggle.addEventListener('click', () => {
        const expanded = manualToggle.getAttribute('aria-expanded') === 'true';
        if (expanded) collapseManual();
        else expandManual();
      });
    }

    if (changeBtn instanceof HTMLButtonElement) {
      changeBtn.addEventListener('click', () => {
        root.dataset.vehicleEditing = 'true';
        setMode('find');
        expandManual();
      });
    }

    const dialog = root.closest('dialog');
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('close', () => {
        root.dataset.vehicleEditing = '';
        syncMode();
      });
    }

    window.addEventListener('map:vehicle-selected', (event) => {
      if (!(event instanceof CustomEvent && event.detail)) {
        root.dataset.vehicleEditing = '';
      }
      if (!root.dataset.vehicleEditing) syncMode();
    });

    syncMode();
  }

  function initVehicleFindPanels() {
    document.querySelectorAll('[data-vehicle-find-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) initVehicleFindPanel(panel);
    });
  }

  /** @param {HTMLElement} root */
  function initLocationFindPanel(root) {
    if (root.dataset.locationFindInit === 'true') return;
    root.dataset.locationFindInit = 'true';

    const searchInput = root.querySelector('[data-location-find-search]');
    const searchBtn = root.querySelector('[data-location-find-search-btn]');
    const geolocateBtn = root.querySelector('[data-location-find-geolocate]');
    const results = root.querySelector('[data-location-find-results]');
    const status = root.querySelector('[data-location-find-status]');
    let loading = false;

    /** @param {string} message */
    function showStatus(message) {
      if (!(status instanceof HTMLElement)) return;
      status.textContent = message;
      status.hidden = false;
    }

    function hideStatus() {
      if (status instanceof HTMLElement) status.hidden = true;
    }

    /** @param {string} value */
    function syncSearchState(value) {
      const hasValue = value.trim().length > 0;
      if (searchBtn instanceof HTMLButtonElement) searchBtn.disabled = loading || !hasValue;
    }

    /** @param {boolean} value */
    function setLoading(value) {
      loading = value;
      if (searchInput instanceof HTMLInputElement) searchInput.disabled = value;
      if (geolocateBtn instanceof HTMLButtonElement) geolocateBtn.disabled = value;
      syncSearchState(searchInput instanceof HTMLInputElement ? searchInput.value : '');
    }

    /** @param {unknown} error */
    function apiErrorMessage(error) {
      if (error instanceof Error && error.message === 'config') {
        return root.dataset.locationConfigError || 'Store search is not configured.';
      }
      if (error instanceof Error && error.message === 'geocode') {
        return root.dataset.locationGeocodeError || "We couldn't find that location.";
      }
      if (error instanceof Error && error.message === 'timeout') {
        return root.dataset.locationTimeoutError || 'Store search timed out. Please try again.';
      }
      return root.dataset.locationApiError || 'Pickup stores are temporarily unavailable.';
    }

    /** @param {Array<Record<string, string>>} stores */
    function renderResults(stores) {
      if (!(results instanceof HTMLElement)) return;
      results.replaceChildren();
      results.hidden = stores.length === 0;

      stores.forEach((store, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'location-find-panel__result';
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
        button.innerHTML = `
          <span class="location-find-panel__result-name"></span>
          <span class="location-find-panel__result-address"></span>
          <span class="location-find-panel__result-availability"></span>
        `;

        const name = button.querySelector('.location-find-panel__result-name');
        const address = button.querySelector('.location-find-panel__result-address');
        const availability = button.querySelector('.location-find-panel__result-availability');
        if (name) name.textContent = store.name || '';
        if (address) {
          address.textContent = [store.addressLine, store.postcode].filter(Boolean).join(' ');
        }
        if (availability) {
          availability.textContent = store.availability || '';
          availability.hidden = !store.availability;
        }

        button.addEventListener('click', () => selectStore(store, button));
        results.appendChild(button);
      });
    }

    /** @param {Record<string, string>} store @param {HTMLButtonElement | null} button */
    function selectStore(store, button) {
      const clickCollect = window.MapClickCollect;
      if (!clickCollect || typeof clickCollect.writeStoredLocation !== 'function') return;
      clickCollect.writeStoredLocation(store);
      results?.querySelectorAll('[aria-pressed="true"]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
      button?.setAttribute('aria-pressed', 'true');
      const prefix = root.dataset.locationSelectedMessage || 'Your nearest pickup store is';
      showStatus(`${prefix} ${store.name}.`);
    }

    /** @param {unknown} value */
    function useSearchResults(value) {
      const stores = Array.isArray(value) ? value : [];
      renderResults(stores);
      if (!stores.length) {
        showStatus(root.dataset.locationNoResults || 'No pickup stores were found nearby.');
        return;
      }
      selectStore(stores[0], results?.querySelector('.location-find-panel__result') || null);
    }

    /** @param {string} query */
    async function searchByQuery(query) {
      const clickCollect = window.MapClickCollect;
      if (!clickCollect || typeof clickCollect.searchLocationsByQuery !== 'function') {
        showStatus(root.dataset.locationApiError || 'Pickup stores are temporarily unavailable.');
        return;
      }

      setLoading(true);
      showStatus(root.dataset.locationSearchingMessage || 'Finding your nearest pickup stores…');
      renderResults([]);
      try {
        useSearchResults(await clickCollect.searchLocationsByQuery(query));
      } catch (error) {
        showStatus(apiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }

    async function searchByBrowserLocation() {
      if (!navigator.geolocation) {
        showStatus(root.dataset.locationUnsupported || 'Your browser does not support location access.');
        return;
      }

      const clickCollect = window.MapClickCollect;
      if (!clickCollect || typeof clickCollect.searchLocationsByCoordinates !== 'function') {
        showStatus(root.dataset.locationApiError || 'Pickup stores are temporarily unavailable.');
        return;
      }

      setLoading(true);
      showStatus(root.dataset.locationSearchingMessage || 'Finding your nearest pickup stores…');
      renderResults([]);

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 300000,
          });
        });
        const stores = await clickCollect.searchLocationsByCoordinates(
          position.coords.latitude,
          position.coords.longitude,
        );
        useSearchResults(stores);
      } catch (error) {
        const geolocationCode =
          typeof error === 'object' && error !== null && 'code' in error ? Number(error.code) : 0;
        if (geolocationCode === 1) {
          showStatus(root.dataset.locationPermissionDenied || 'Location permission was not granted.');
        } else if (geolocationCode === 3) {
          showStatus(root.dataset.locationTimeoutError || 'Store search timed out. Please try again.');
        } else {
          showStatus(apiErrorMessage(error));
        }
      } finally {
        setLoading(false);
      }
    }

    if (searchInput instanceof HTMLInputElement) {
      syncSearchState(searchInput.value);
      searchInput.addEventListener('input', () => {
        syncSearchState(searchInput.value);
        hideStatus();
      });
      searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && searchBtn instanceof HTMLButtonElement && !searchBtn.disabled) {
          event.preventDefault();
          searchBtn.click();
        }
      });
    }

    if (searchBtn instanceof HTMLButtonElement && searchInput instanceof HTMLInputElement) {
      searchBtn.addEventListener('click', () => {
        const query = searchInput.value.trim();
        if (!query) {
          showStatus(root.dataset.locationEmptyMessage || 'Enter a suburb or postcode to search.');
          return;
        }
        void searchByQuery(query);
      });
    }

    if (geolocateBtn instanceof HTMLButtonElement) {
      geolocateBtn.addEventListener('click', () => {
        void searchByBrowserLocation();
      });
    }

    const storedLocation =
      window.MapClickCollect && typeof window.MapClickCollect.readStoredLocation === 'function'
        ? window.MapClickCollect.readStoredLocation()
        : null;
    if (storedLocation) {
      renderResults([storedLocation]);
      const prefix = root.dataset.locationSelectedMessage || 'Your nearest pickup store is';
      showStatus(`${prefix} ${storedLocation.name}.`);
    }
  }

  function initLocationFindPanels() {
    document.querySelectorAll('[data-location-find-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) initLocationFindPanel(panel);
    });
  }

  function initModals() {
    document.querySelectorAll('[data-map-modal]').forEach((dialog) => {
      if (dialog instanceof HTMLDialogElement) bindModal(dialog);
    });

    document.querySelectorAll('[data-map-drawer]').forEach((dialog) => {
      if (dialog instanceof HTMLDialogElement) bindDrawer(dialog);
    });

    initVehicleFindPanels();
    initLocationFindPanels();
  }

  /** @type {Window & { MapUI?: Record<string, unknown> }} */
  const win = window;

  /**
   * @param {HTMLDialogElement} dialog
   */
  function openDrawer(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.dispatchEvent(new CustomEvent('map:drawer-open'));
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {{ immediate?: boolean }} [options]
   */
  function closeDrawer(dialog, options = {}) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.dispatchEvent(new CustomEvent('map:drawer-close', { detail: options }));
  }

  win.MapUI = {
    getFocusable,
    createFocusTrap,
    initModals,
    initVehicleFindPanel,
    initLocationFindPanel,
    openDrawer,
    closeDrawer,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModals);
  } else {
    initModals();
  }
})();
